/**
 * Offline Sync Queue — queues Supabase mutations when offline, replays when back online.
 *
 * Uses in-memory storage (no IndexedDB/localStorage dependency) with periodic flush.
 * For POS critical operations: sales, sale_items, stock_movements, payment_transactions.
 *
 * Usage:
 *   import { queueMutation, startSyncWorker } from '@/services/offlineSyncQueue';
 *
 *   // In App.tsx or main provider:
 *   startSyncWorker();
 *
 *   // When creating a sale offline:
 *   queueMutation({ table: 'sales', operation: 'insert', data: saleRow });
 */

import { supabase } from '@/integrations/supabase/client';
import { devLog } from '@/lib/logger';
import type { Database } from '@/integrations/supabase/types';

// Pivot pour les accès dynamiques par nom de table.
// Le cast vers 'profiles' évite l'explosion de type (TS2589) due aux ~130 tables
// du schéma quand on passe table: string à supabase.from(). Les méthodes CRUD
// (insert/update/upsert/delete) sont structurellement identiques pour toutes les tables.
type _PivotTable = Database['public']['Tables']['profiles'];
type _PivotInsert = _PivotTable['Insert'];
type _PivotUpdate = _PivotTable['Update'];
type _PivotRow = _PivotTable['Row'];
type _PivotCol = string & keyof _PivotRow;

// ─── Types ──────────────────────────────────────────────────

export interface QueuedMutation {
  id: string;
  table: string;
  operation: 'insert' | 'update' | 'upsert' | 'delete';
  data: Record<string, unknown>;
  matchColumn?: string; // for update/delete: column to match on
  matchValue?: string; // for update/delete: value to match
  createdAt: number;
  retries: number;
  lastError?: string;
}

export interface SyncQueueStatus {
  online: boolean;
  queueLength: number;
  syncing: boolean;
  lastSyncAt: number | null;
  lastError: string | null;
}

// ─── Queue storage (in-memory) ──────────────────────────────

let queue: QueuedMutation[] = [];
let syncing = false;
let lastSyncAt: number | null = null;
let lastError: string | null = null;
const MAX_RETRIES = 5;
const RETRY_DELAY_MS = 3000;
const SYNC_INTERVAL_MS = 10000; // check every 10s
let intervalId: ReturnType<typeof setInterval> | null = null;
let listeners: Array<(status: SyncQueueStatus) => void> = [];

// ─── Public API ─────────────────────────────────────────────

/** Queue a mutation for offline replay */
export function queueMutation(
  mutation: Omit<QueuedMutation, 'id' | 'createdAt' | 'retries'>,
): void {
  const entry: QueuedMutation = {
    ...mutation,
    id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    createdAt: Date.now(),
    retries: 0,
  };
  queue.push(entry);
  notifyListeners();
  devLog(`[SyncQueue] Queued ${mutation.operation} on ${mutation.table} (queue: ${queue.length})`);

  // Attempt immediate flush if online
  if (navigator.onLine && !syncing) {
    flushQueue();
  }
}

/** Get current queue status */
export function getQueueStatus(): SyncQueueStatus {
  return {
    online: navigator.onLine,
    queueLength: queue.length,
    syncing,
    lastSyncAt,
    lastError,
  };
}

/** Subscribe to status changes */
export function onSyncStatusChange(callback: (status: SyncQueueStatus) => void): () => void {
  listeners.push(callback);
  return () => {
    listeners = listeners.filter((l) => l !== callback);
  };
}

/** Start the background sync worker */
export function startSyncWorker(): void {
  if (intervalId) return; // already running

  // Listen for online/offline events
  window.addEventListener('online', () => {
    devLog('[SyncQueue] Back online — flushing queue');
    notifyListeners();
    flushQueue();
  });

  window.addEventListener('offline', () => {
    devLog('[SyncQueue] Went offline');
    notifyListeners();
  });

  // Periodic sync attempt
  intervalId = setInterval(() => {
    if (navigator.onLine && queue.length > 0 && !syncing) {
      flushQueue();
    }
  }, SYNC_INTERVAL_MS);

  devLog('[SyncQueue] Sync worker started');
}

/** Stop the background sync worker */
export function stopSyncWorker(): void {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
}

/** Get pending queue entries (for debugging/display) */
export function getQueueEntries(): QueuedMutation[] {
  return [...queue];
}

/** Clear all queued mutations (danger!) */
export function clearQueue(): void {
  queue = [];
  notifyListeners();
}

// ─── Internal flush logic ───────────────────────────────────

async function flushQueue(): Promise<void> {
  if (syncing || queue.length === 0 || !navigator.onLine) return;

  syncing = true;
  lastError = null;
  notifyListeners();

  const toProcess = [...queue];
  const succeeded: string[] = [];
  const failed: string[] = [];

  for (const entry of toProcess) {
    try {
      await executeMutation(entry);
      succeeded.push(entry.id);
    } catch (err: any) {
      entry.retries++;
      entry.lastError = err.message || 'Unknown error';

      if (entry.retries >= MAX_RETRIES) {
        console.error(`[SyncQueue] Max retries reached for ${entry.id}, dropping`, entry);
        failed.push(entry.id);
        lastError = `Échec définitif: ${entry.table}.${entry.operation} — ${entry.lastError}`;
      } else {
        console.warn(
          `[SyncQueue] Retry ${entry.retries}/${MAX_RETRIES} for ${entry.id}:`,
          err.message,
        );
      }

      // If we got a network error, stop processing the rest
      if (!navigator.onLine) break;

      // Brief delay between retries
      await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
    }
  }

  // Remove succeeded and max-retried entries
  const toRemove = new Set([...succeeded, ...failed]);
  queue = queue.filter((e) => !toRemove.has(e.id));

  if (succeeded.length > 0) {
    lastSyncAt = Date.now();
    devLog(`[SyncQueue] Synced ${succeeded.length} mutations, ${queue.length} remaining`);
  }

  syncing = false;
  notifyListeners();
}

async function executeMutation(entry: QueuedMutation): Promise<void> {
  const { table, operation, data, matchColumn, matchValue } = entry;

  // Cast vers 'profiles' pour éviter TS2589 (union ~130 tables trop profonde).
  // Toutes les tables partagent la même API CRUD structurelle.
  const from = supabase.from(table as 'profiles');
  let result: { error: unknown };

  switch (operation) {
    case 'insert':
      result = await from.insert(data as _PivotInsert);
      break;
    case 'update':
      if (!matchColumn || !matchValue)
        throw new Error('update requires matchColumn and matchValue');
      result = await from
        .update(data as _PivotUpdate)
        .eq(matchColumn as _PivotCol, matchValue);
      break;
    case 'upsert':
      result = await from.upsert(data as _PivotInsert);
      break;
    case 'delete':
      if (!matchColumn || !matchValue)
        throw new Error('delete requires matchColumn and matchValue');
      result = await from.delete().eq(matchColumn as _PivotCol, matchValue);
      break;
    default:
      throw new Error(`Unknown operation: ${operation}`);
  }

  if (result.error) throw result.error;
}

function notifyListeners(): void {
  const status = getQueueStatus();
  listeners.forEach((l) => {
    try {
      l(status);
    } catch {}
  });
}
